import {
  CartItem,
  CartItemGroup,
  CartItemGroups,
  CartItemOption,
} from '@open-tender/types'
import { calcPrices } from '@open-tender/utils'

export type BrandWithAssortment = {
  uses_catering_assortment_builder?: boolean
}

// A slot is a top-level group that needs exactly one choice, e.g. "Option 1"
// holding Italian / Club.
const isSlotGroup = (group: CartItemGroup): boolean =>
  !group.isSize &&
  !group.isPizza &&
  group.min === 1 &&
  group.max === 1 &&
  group.options.length > 1

// Every non-size group must be a slot, otherwise the assortment screen would
// silently hide groups the customer needs to see.
export const getAssortmentSlots = (item: CartItem): CartItemGroups | null => {
  const groups = item.groups ?? []
  if (groups.some((group) => group.isSize)) return null
  if (groups.length < 2 || !groups.every(isSlotGroup)) return null
  return groups
}

// Brand-level feature flag (Brand Feature Flags in the admin), catering only.
export const isAssortmentEnabled = (
  brand: BrandWithAssortment | null | undefined,
  orderType: string | null | undefined
): boolean =>
  orderType === 'CATERING' && brand?.uses_catering_assortment_builder === true

export const isAssortmentItem = (
  item: CartItem | null | undefined,
  enabled: boolean
): boolean => !!item && enabled && getAssortmentSlots(item) !== null

export const getSelectedOption = (
  group: CartItemGroup
): CartItemOption | null => group.options.find((o) => o.quantity > 0) ?? null

export const countFilledSlots = (slots: CartItemGroups): number =>
  slots.filter((slot) => getSelectedOption(slot) !== null).length

export const clearSlots = (item: CartItem): CartItem => {
  const groups = item.groups.map((group) => ({
    ...group,
    options: group.options.map((option) => ({ ...option, quantity: 0 })),
  }))
  return calcPrices({ ...item, groups }) as CartItem
}

// Selects one option in a slot and restores that option's nested choices to
// their menu defaults, so a swap never carries over the previous customizations.
export const selectSlotOption = (
  item: CartItem,
  pristine: CartItem,
  groupId: number,
  optionId: number
): CartItem => {
  const pristineGroup = pristine.groups.find((g) => g.id === groupId)
  const pristineOption = pristineGroup?.options.find((o) => o.id === optionId)
  const groups = item.groups.map((group) => {
    if (group.id !== groupId) return group
    const options = group.options.map((option) => {
      if (option.id !== optionId) return { ...option, quantity: 0 }
      return { ...(pristineOption ?? option), quantity: 1 }
    })
    return { ...group, options }
  })
  return calcPrices({ ...item, groups }) as CartItem
}

const withQuantity = (name: string, quantity: number): string =>
  quantity > 1 ? `${name} x${quantity}` : name

const collectChanges = (
  groups: CartItemGroups,
  parent: CartItemOption | null,
  changes: string[]
): void => {
  groups.forEach((group) => {
    group.options.forEach((option) => {
      if (option.quantity <= 0) {
        // A default ingredient taken out of a multi-select group. Radio groups
        // (bread, cheese) swap one default for another, which is not a removal.
        if (option.isDefault && group.max !== 1)
          changes.push(`No ${option.name}`)
        return
      }
      if (!option.isDefault) {
        // Choices made under an ingredient read as "No Smoked Ham"; choices made
        // directly under the sandwich keep their own name ("Multi Grain").
        const label = parent ? `${option.name} ${parent.name}` : option.name
        changes.push(withQuantity(label, option.quantity))
      }
      collectChanges(option.groups, option, changes)
    })
  })
}

// Lists only what differs from the menu defaults for a selected slot option.
export const getSlotChanges = (option: CartItemOption | null): string[] => {
  if (!option) return []
  const changes: string[] = []
  collectChanges(option.groups, null, changes)
  return Array.from(new Set(changes))
}

// A group of ingredients that each carry a single two-way choice, e.g.
// "Included" -> Smoked Ham -> Regular | No.
export const isIngredientGroup = (group: CartItemGroup): boolean =>
  group.options.length > 0 &&
  group.options.every(
    (option) =>
      option.groups.length === 1 && option.groups[0].options.length === 2
  )
